import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, deactivateTenant, deactivateTenantUrl, describeError, newIdempotencyKey } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { stepUpOptions, useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import { Badge } from '@/components/ui';
import { StepUpDialog } from '@/components/StepUpDialog';

type Tenant = {
  id: string;
  tradeName: string;
  isActive: boolean;
  subscriptionStatus: string;
  deactivationReason?: string | null;
  deactivationDetail?: string | null;
  deactivatedAt?: string | null;
};

/**
 * Mirrors TenantDeactivationReason on the server. There is deliberately no
 * option for non-payment: overdue tenants are already blocked by their
 * subscription status, and that block lifts when they pay. Disabling one here
 * would create a state payment cannot recover from.
 */
const REASONS = [
  { value: 'ContractEnded', label: 'Encerramento de contrato' },
  { value: 'CustomerRequest', label: 'Pedido do cliente' },
  { value: 'Fraud', label: 'Fraude ou uso indevido' },
  { value: 'LegalCompliance', label: 'Determinação legal' },
  { value: 'AbuseOrMisuse', label: 'Violação dos termos de uso' },
  { value: 'InternalOrTest', label: 'Tenant interno, duplicado ou de teste' },
  { value: 'Other', label: 'Outro' },
] as const;

const BILLING_BLOCKED = ['pastdue', 'expired'];

/** O mesmo mínimo do campo de detalhe (`minLength`). */
const MIN_DETAIL_LENGTH = 5;

type DeactivationBody = { reasonCategory: string; reasonDetail: string };

/** O `fieldset` que trava o formulário enquanto o modal do step-up está aberto, sem moldura. */
const LOCK_FIELDSET_STYLE = { border: 0, padding: 0, margin: 0, minWidth: 0 } as const;

export function TenantLifecyclePage() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [category, setCategory] = useState<string>('ContractEnded');
  const [detail, setDetail] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [billingStillActive, setBillingStillActive] = useState(false);
  const idempotency = useIdempotencyKey();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/platform/tenants/${tenantId}`);
      setTenant(data);
      setError(null);
    } catch (err) {
      setError(describeError(err));
    }
  }, [tenantId]);

  /*
   * Falso positivo da regra, nao divida: todo `setState` deste callback roda
   * DEPOIS do `await`, que e o que a propria documentacao aponta como valido.
   * O bug esta registrado em facebook/react#34905, ainda aberto.
   *
   * Revisar quando o plugin subir de versao: se a supressao virar "unused
   * eslint-disable", e sinal de que corrigiram e a linha pode sair.
   */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- ver nota acima
    void load();
  }, [load]);

  const billingBlocked = tenant
    ? BILLING_BLOCKED.includes(tenant.subscriptionStatus?.toLowerCase())
    : false;

  // Motivo e confirmação explícita antes do modal de TOTP: sem os dois, nem o modal abre.
  const detailValid = detail.trim().length >= MIN_DETAIL_LENGTH;
  const canSubmit = confirmed && detailValid;

  /**
   * A desativação com o código do step-up (C6). O corpo é o que o operador confirmou ao abrir o
   * modal (`startDeactivation`), congelado pelo `useStepUpDialog`, e não o estado vivo do
   * formulário. A chave é a da tentativa: a repetição depois do 403 de step-up (código errado ou
   * já usado) sai com a mesma, e só o sucesso ou um corpo diferente trazem chave nova
   * (`useIdempotencyKey`).
   */
  async function deactivate(proof: StepUpProof, body: DeactivationBody) {
    if (!tenantId) return;
    const key = idempotency.keyFor(deactivateTenantUrl(tenantId), body);
    let data;
    try {
      data = await deactivateTenant(tenantId, body, { idempotencyKey: key, ...stepUpOptions(proof) });
      idempotency.settle();
    } catch (err) {
      idempotency.settle(err);
      throw err;
    }
    setConfirmed(false);
    setDetail('');
    setBillingStillActive(Boolean(data.activeGatewaySubscription));
    setNotice(
      data.activeGatewaySubscription
        ? 'Tenant desativado. Atenção: a assinatura segue ativa no gateway e continuará cobrando. ' +
            'Cancele a assinatura separadamente se for o caso.'
        : 'Tenant desativado.',
    );
    await load();
  }

  const stepUp = useStepUpDialog(deactivate, (err) => setError(describeError(err)), {
    // O canSubmit de novo no envio do código, sobre o corpo congelado.
    isValid: (body) => confirmed && body.reasonDetail.length >= MIN_DETAIL_LENGTH,
    // Passkey (C14): o desafio preso a este tenant.
    passkeyTarget: () => (tenantId ? stepUpTarget('tenantDeactivate', tenantId) : null),
  });

  function startDeactivation(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || stepUp.open) return;
    setError(null);
    setNotice(null);
    setBillingStillActive(false);
    stepUp.start({ reasonCategory: category, reasonDetail: detail.trim() });
  }

  async function activate() {
    const why = window.prompt('Motivo da reativação (obrigatório):');
    if (!why || !why.trim()) return;

    setError(null);
    setNotice(null);
    try {
      const { data } = await api.post(
        `/platform/tenants/${tenantId}/activate`,
        { reason: why.trim() },
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      setNotice(data.note ?? 'Tenant reativado.');
      await load();
    } catch (err) {
      setError(describeError(err));
    }
  }

  if (!tenant) {
    return (
      <div className="page">
        <Link to={`/tenants/${tenantId}`} className="backlink">← Tenant</Link>
        {error ? <div className="error-banner">{error}</div> : <p>Carregando…</p>}
      </div>
    );
  }

  return (
    <div className="page" style={{ maxWidth: 720 }}>
      <Link to={`/tenants/${tenantId}`} className="backlink">← Tenant</Link>
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
        Ciclo de vida do tenant
      </h2>

      {error && <div className="error-banner">{error}</div>}
      {notice && (
        <div className="info-banner">
          {notice}
          {billingStillActive && (
            <>
              {' '}
              <Link to={`/tenants/${tenantId}`}>Cancelar a assinatura no detalhe do tenant</Link>.
            </>
          )}
        </div>
      )}

      <div className="card">
        <div className="kv">
          <div className="k">Estado administrativo</div>
          <div>
            {tenant.isActive
              ? <Badge tone="success">Habilitado</Badge>
              : <Badge tone="error">Desativado</Badge>}
          </div>
          <div className="k">Status comercial</div>
          <div>{tenant.subscriptionStatus}</div>
          {!tenant.isActive && (
            <>
              <div className="k">Motivo</div>
              <div>{tenant.deactivationReason ?? '—'} — {tenant.deactivationDetail ?? '—'}</div>
            </>
          )}
        </div>
      </div>

      {tenant.isActive ? (
        <form className="card" onSubmit={startDeactivation} aria-label="Desativar tenant">
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Desativar tenant</h3>

          {/* Com o modal do step-up aberto, motivo, detalhe, confirmação e botão ficam travados: o
              que sai é o que o operador confirmou ao abrir o modal. */}
          <fieldset disabled={stepUp.open} style={LOCK_FIELDSET_STYLE}>
            {/* Deactivating an overdue tenant is the one mistake this screen can
                cause that nothing recovers from automatically — the customer pays,
                the status goes back to Active, and the store stays dark. */}
            {billingBlocked && (
              <div className="info-banner" style={{ background: '#FEF3C7', color: '#92400E', borderColor: '#FDE68A' }}>
                Este tenant já está bloqueado por <strong>{tenant.subscriptionStatus}</strong>.
                O bloqueio por inadimplência é automático e se resolve sozinho quando o cliente paga.
                Desativar agora <strong>impede essa reativação automática</strong>: o pagamento entrará,
                o status voltará a ativo, e a loja continuará fora do ar até um Admin reativar.
              </div>
            )}
  
            <div className="info-banner" style={{ background: '#FEE2E2', color: '#991B1B', borderColor: '#FECACA' }}>
              O painel e o cardápio público ficarão <strong>indisponíveis imediatamente</strong> e as
              sessões abertas serão encerradas. A cobrança no gateway <strong>não</strong> é cancelada
              por esta ação.
            </div>
  
            <div className="field">
              <label htmlFor="category">Motivo</label>
              <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
                {REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
  
            <div className="field">
              <label htmlFor="detail">Detalhe (obrigatório)</label>
              <textarea
                id="detail"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                rows={3}
                required
                minLength={MIN_DETAIL_LENGTH}
              />
            </div>
  
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span>
                Confirmo que quero tirar <strong>{tenant.tradeName}</strong> do ar.
              </span>
            </label>
  
            {/* O botão abre o modal do código do autenticador (step-up, C6); a desativação só
                sai de lá. */}
            <button type="submit" className="btn btn-danger" disabled={stepUp.busy || !canSubmit}>
              {stepUp.busy ? 'Desativando…' : 'Desativar tenant'}
            </button>
          </fieldset>
        </form>
      ) : (
        <div className="card">
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Reativar tenant</h3>
          <p>
            Reativar devolve o controle administrativo. O acesso efetivo ainda depende do status
            comercial: se ele não conceder acesso, será necessária concessão ou nova assinatura.
          </p>
          <button className="btn" onClick={() => void activate()}>Reativar tenant</button>
        </div>
      )}

      {/* Fora do formulário: o modal tem o formulário dele. */}
      {stepUp.open && (
        <StepUpDialog
          title={`Desativar ${tenant.tradeName}`}
          description={
            <>
              <p style={{ margin: '0 0 8px' }}>
                O painel e o cardápio público ficarão <strong>indisponíveis imediatamente</strong> e as
                sessões abertas serão encerradas.
              </p>
              <p style={{ margin: 0 }}>
                A cobrança recorrente <strong>segue ativa</strong> no gateway quando houver assinatura:
                cancelar a assinatura é uma ação separada, no detalhe do tenant.
              </p>
            </>
          }
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        />
      )}
    </div>
  );
}
